/**
 * IncomingCallModal — global ringing UI mounted in the doctor's dashboard.
 *
 * Polls /api/voice/incoming every 5s when the dashboard is focused. When a
 * pending/ringing call is detected, shows a fullscreen ringing modal with
 * Accept / Reject. On Accept, opens a WebRTC peer (callee role) and exchanges
 * SDP/ICE via the signal endpoints.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, CheckCircle2, X } from 'lucide-react';
import api from '@/lib/api';
import { VoicePeer, bindRemoteAudio } from '@/lib/webrtc';

const POLL_FOCUSED_MS = 5000;
const POLL_BLURRED_MS = 30000;

export default function IncomingCallModal() {
  const [pending, setPending] = useState(null); // {id, patient_name, patient_mobile, mode, ...}
  const [step, setStep] = useState('idle'); // idle | ringing | active | ended
  const [iceServers, setIceServers] = useState([]);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState('');
  const peerRef = useRef(null);
  const sigPollRef = useRef(null);
  const sigSeqRef = useRef(0);
  const audioRef = useRef(null);
  const startTimeRef = useRef(null);
  const tickRef = useRef(null);
  const ringRef = useRef(null);

  const stopAll = () => {
    if (sigPollRef.current) clearInterval(sigPollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    sigPollRef.current = null; tickRef.current = null;
    if (peerRef.current) { try { peerRef.current.close(); } catch {} peerRef.current = null; }
    if (ringRef.current) { try { ringRef.current.pause(); ringRef.current.currentTime = 0; } catch {} }
  };

  // Poll for incoming calls
  useEffect(() => {
    let cancelled = false;
    let interval = POLL_FOCUSED_MS;
    let timer = null;

    const poll = async () => {
      if (cancelled || step === 'active') return;
      try {
        const r = await api.get('/voice/incoming');
        if (cancelled) return;
        const calls = r.data.calls || [];
        if (calls.length && step === 'idle') {
          setPending(calls[0]);
          setIceServers(r.data.ice_servers || []);
          setStep('ringing');
          // Play ringtone (web audio bell)
          try {
            ringRef.current = new Audio('data:audio/wav;base64,UklGRoQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YWAAAAA=');
            ringRef.current.loop = true;
            ringRef.current.volume = 0.3;
            ringRef.current.play().catch(() => {});
          } catch {}
        }
      } catch {}
    };

    const schedule = () => {
      timer = setTimeout(async () => {
        await poll();
        schedule();
      }, document.hidden ? POLL_BLURRED_MS : interval);
    };

    const onVisChange = () => {
      if (timer) clearTimeout(timer);
      schedule();
    };
    document.addEventListener('visibilitychange', onVisChange);
    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [step]);

  // SW push fast-path: if a 'incoming_call' notification fires, we immediately poll
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'incoming_call') {
        // trigger immediate poll on next tick
        api.get('/voice/incoming').then((r) => {
          if (r.data.calls?.length && step === 'idle') {
            setPending(r.data.calls[0]);
            setIceServers(r.data.ice_servers || []);
            setStep('ringing');
          }
        }).catch(() => {});
      }
    };
    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener('message', handler);
    return () => { if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener('message', handler); };
  }, [step]);

  const accept = async () => {
    if (!pending) return;
    try { ringRef.current?.pause(); } catch {}
    try {
      const ar = await api.post(`/voice/call/${pending.id}/accept`);
      const servers = ar.data.ice_servers || iceServers;
      setIceServers(servers);
      setStep('active');
      startTimeRef.current = Date.now();
      tickRef.current = setInterval(() => setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);

      const peer = new VoicePeer({
        role: 'callee',
        iceServers: servers,
        onState: (s) => {
          if (s === 'failed' || s === 'closed') {
            setStep('ended');
            stopAll();
          }
        },
        onRemoteStream: (stream) => bindRemoteAudio(audioRef.current, stream),
        onError: (msg) => setError(msg),
        onLocalSignal: async ({ type, payload }) => {
          try { await api.post(`/voice/call/${pending.id}/signal`, { type, payload }); } catch {}
        },
      });
      peerRef.current = peer;
      await peer.startCallee();

      // Begin polling for the patient's offer + their ICE
      let interval = 1500;
      const sigPoll = async () => {
        if (!peerRef.current) return;
        try {
          const r = await api.get(`/voice/call/${pending.id}/signal`, { params: { since: sigSeqRef.current } });
          for (const s of r.data.signals || []) {
            sigSeqRef.current = Math.max(sigSeqRef.current, s.seq);
            await peer.receiveSignal({ type: s.type, payload: s.payload });
          }
          if (peer.pc?.connectionState === 'connected') interval = 5000;
        } catch {}
      };
      sigPollRef.current = setInterval(sigPoll, interval);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not accept call');
      setStep('ended');
      stopAll();
    }
  };

  const reject = async () => {
    if (!pending) return;
    try { await api.post(`/voice/call/${pending.id}/reject`); } catch {}
    stopAll();
    setStep('idle'); setPending(null);
  };

  const hangup = async () => {
    if (pending) {
      try { await api.post(`/voice/call/${pending.id}/signal`, { type: 'hangup', payload: {} }); } catch {}
    }
    stopAll();
    setStep('ended');
  };

  const dismiss = () => {
    stopAll(); setStep('idle'); setPending(null); setDuration(0); setError('');
  };

  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (step === 'idle' || !pending) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" data-testid="incoming-call-modal">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <audio ref={audioRef} autoPlay playsInline />

        {step === 'ringing' && (
          <div className="p-8 text-center">
            <div className="w-24 h-24 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-5 animate-pulse">
              <Phone size={36} className="text-emerald-600" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-2">Incoming voice call</p>
            <p className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="incoming-caller-name">
              {pending.patient_name}
            </p>
            <p className="text-sm text-slate-500 mb-1">{pending.patient_mobile}</p>
            {pending.note && <p className="text-xs text-slate-600 mt-3 px-4 py-2 bg-slate-50 rounded-xl italic">"{pending.note}"</p>}
            <div className="flex justify-center gap-4 mt-6">
              <button onClick={reject}
                className="inline-flex flex-col items-center gap-1 px-5 py-3.5 bg-rose-600 text-white rounded-2xl text-xs font-semibold hover:bg-rose-700"
                data-testid="incoming-reject">
                <PhoneOff size={20} />Reject
              </button>
              <button onClick={accept}
                className="inline-flex flex-col items-center gap-1 px-5 py-3.5 bg-emerald-600 text-white rounded-2xl text-xs font-semibold hover:bg-emerald-700"
                data-testid="incoming-accept">
                <Phone size={20} />Accept
              </button>
            </div>
          </div>
        )}

        {step === 'active' && (
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 size={32} className="text-emerald-700" />
            </div>
            <p className="text-base font-semibold text-slate-900 mb-1">Connected · {pending.patient_name}</p>
            <p className="text-xs text-emerald-700 font-mono mb-6" data-testid="incoming-duration">{fmt(duration)}</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => { setMuted(!muted); peerRef.current?.setMicMuted(!muted); }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ${muted ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="incoming-mute">
                {muted ? <MicOff size={14} /> : <Mic size={14} />}{muted ? 'Unmute' : 'Mute'}
              </button>
              <button onClick={hangup}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700"
                data-testid="incoming-end">
                <PhoneOff size={14} />End call
              </button>
            </div>
            {error && <p className="text-[11px] text-amber-600 mt-3">{error}</p>}
          </div>
        )}

        {step === 'ended' && (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4">
              <PhoneOff size={20} className="text-slate-500" />
            </div>
            <p className="text-sm font-semibold text-slate-900 mb-1">Call ended</p>
            {duration > 0 && <p className="text-[11px] text-slate-400 mt-1">Duration: {fmt(duration)}</p>}
            <button onClick={dismiss} className="mt-5 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium" data-testid="incoming-dismiss">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
